import type { Editor } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import fuzzysort from 'fuzzysort';
import tippy, { type Instance } from 'tippy.js';
import { slashMenuCommands } from '../../constants/slashMenuCommands';
import type { SuggestionsMenuRef } from './SuggestionsMenu';
import { SuggestionsMenu } from './SuggestionsMenu';

export const suggestions = {
  items: ({ query: q }: { query: string }) => {
    const query = q.toLowerCase().trim();

    if (!query) {
      return slashMenuCommands;
    }

    const fuzzyResults = fuzzysort.go(query, slashMenuCommands, { key: 'name' }).map((item) => ({
      ...item,
      highlightedName: fuzzysort.highlight(item, '<b>', '</b>'),
    }));

    return fuzzyResults.map(({ obj, highlightedName }) => ({
      ...obj,
      highlightedName,
    }));
  },

  render: () => {
    let component: ReactRenderer;
    let popup: Instance[];
    let localProps: Record<string, unknown> | undefined;

    return {
      onStart: (props: Record<string, unknown> | undefined) => {
        localProps = { ...props, event: '' };

        component = new ReactRenderer(SuggestionsMenu, {
          props: localProps,
          editor: localProps?.editor as Editor,
        });

        popup = tippy('body', {
          getReferenceClientRect: localProps.clientRect as () => DOMRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          animation: 'shift-toward-subtle',
          duration: 250,
        });
      },

      onUpdate(props: Record<string, unknown> | undefined) {
        localProps = { ...props, event: '' };

        component.updateProps(localProps);
        popup[0]!.setProps({
          getReferenceClientRect: localProps.clientRect as () => DOMRect,
        });
      },

      onKeyDown(props: { event: KeyboardEvent }) {
        component.updateProps({ ...localProps, event: props.event });
        const ref = component.ref as SuggestionsMenuRef | null;
        const handled = ref?.onKeyDown({ event: props.event });

        if (props.event.key === 'Escape') {
          popup[0]!.hide();
          return true;
        }

        return handled ?? false;
      },

      onExit() {
        if (popup && popup[0]) {
          popup[0]?.destroy();
        }
        if (component) {
          component.destroy();
        }
      },
    };
  },
};
